<template>
    <div v-if="lastChangeInfo" class="last-changed-info">
        <div class="last-change-header">Last changed by:</div>
        <div class="last-change-content">
            <Avatar
                :image="userFromStore?.profile_picture ?? undefined"
                :label="
                    userFromStore?.profile_picture
                        ? undefined
                        : getInitials(lastChangeInfo.user_name)
                "
                shape="circle"
                size="normal"
                class="user-avatar"
            />
            <div class="details">
                <span class="username">{{ lastChangeInfo.user_name }}</span>
                <span class="timestamp">{{ formattedDate }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useServices } from "@/lib/openapi";
import { HistoryEntry } from "@/lib/openapi/gen/types.gen";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useUsersManager, useUsersStore } from "@/stores/users-store";
import Avatar from "primevue/avatar";
import { computed, ref, watch } from "vue";

const { isFetching } = useCurrentEntityManager();

const props = defineProps<{
    entityId: string;
    entityType: EntityType;
    attribute?: string;
}>();

interface LastChangeInfo {
    user_name: string;
    photo_url?: string | null;
    timestamp: number;
}

const lastChangeInfo = ref<LastChangeInfo | null>(null);
const { users } = useUsersManager();
const usersStore = useUsersStore();

const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: false,
    },
});

// Find the user in the users store by name
const userFromStore = computed(() => {
    if (!lastChangeInfo.value) return null;
    return users.value.find(
        (user) => user.name === lastChangeInfo.value!.user_name
    );
});

const formattedDate = computed(() => {
    if (!lastChangeInfo.value) return "";

    const date = new Date(lastChangeInfo.value.timestamp);

    // If it's today, show time only
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
        return `at ${date.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
        })}`;
    }

    // If it's this year, show day and month
    if (date.getFullYear() === today.getFullYear()) {
        return `on ${date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
        })}`;
    }

    // Otherwise show full date
    return `on ${date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    })}`;
});

const getInitials = (name: string) => {
    return name
        .split(" ")
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase()
        .substring(0, 2);
};

const fetchLastChangeInfo = async () => {
    if (!props.entityId || !props.entityType) return;

    // Load users to ensure we have profile pictures available
    await usersStore.getAllUsers();

    try {
        // Default to fetching history for the 'name' attribute if not specified
        const attribute = props.attribute || "description";

        const { data, response } =
            await api.startBizEntitiesEntityTypeEntityIdAttributeHistoryGet({
                path: {
                    entity_type: props.entityType,
                    entity_id: props.entityId,
                    attribute,
                },
                query: {
                    limit: 1, // Get only the most recent change
                },
            });

        if (response.ok && data) {
            // Extract the most recent history entry
            const historyEntries = data.history as HistoryEntry[];

            if (historyEntries && historyEntries.length > 0) {
                const latestEntry = historyEntries[0];

                lastChangeInfo.value = {
                    user_name: latestEntry.author.name,
                    photo_url: null, // API doesn't provide this
                    timestamp: new Date(latestEntry.author.date).getTime(),
                };
            }
        }
    } catch (error) {
        console.error("Error fetching entity history:", error);
    }
};

watch(
    isFetching,
    async () => {
        if (!isFetching.value) {
            await fetchLastChangeInfo();
        }
    },
    { immediate: true }
);
</script>

<style scoped lang="scss">
.last-changed-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    white-space: nowrap;
    padding: 0.25rem 0.5rem;
}

.last-change-header {
    font-size: 0.7rem;
    color: var(--text-color) !important;
    margin-bottom: 0.2rem;
}

.last-change-content {
    display: flex;
    align-items: center;
}

.user-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin-right: 0.5rem;
    object-fit: cover;
}

.details {
    display: flex;
    flex-direction: column;
}

.username {
    font-weight: 500;
    font-size: 0.7rem;
}

.timestamp {
    font-size: 0.6rem;
    color: var(--text-color-secondary);
}
</style>
